import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
POLÍTICA DE PRIVACIDAD — EL PROTOCOLO ESTOICO

Última actualización: Marzo 2026

1. INFORMACIÓN QUE RECOPILAMOS
Recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto:
• Nombre completo
• Dirección de correo electrónico
• Información de pago (procesada de forma segura por terceros)

2. USO DE LA INFORMACIÓN
Tu información personal se utiliza exclusivamente para:
• Procesar y confirmar tu compra
• Enviarte el acceso al Protocolo Estoico
• Comunicaciones relacionadas con tu compra (actualizaciones, soporte)

3. PROTECCIÓN DE DATOS
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. NO COMPARTIMOS TU INFORMACIÓN
No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios de pago que procesan tu transacción de forma segura.

5. COOKIES
Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación. Puedes desactivarlas en la configuración de tu navegador.

6. TUS DERECHOS
Tienes derecho a:
• Acceder a tus datos personales
• Solicitar la corrección de datos inexactos
• Solicitar la eliminación de tus datos
• Retirar tu consentimiento en cualquier momento

7. CAMBIOS EN ESTA POLÍTICA
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.
`;
const termsContent = `
TÉRMINOS Y CONDICIONES — EL PROTOCOLO ESTOICO

Última actualización: Marzo 2026

1. ACEPTACIÓN DE TÉRMINOS
Al adquirir El Protocolo Estoico, aceptas estos términos y condiciones en su totalidad. Si no estás de acuerdo, no realices la compra.

2. DESCRIPCIÓN DEL PRODUCTO
El Protocolo Estoico es un programa digital de 21 días en formato PDF que incluye guías de ejercicios, rutinas y estrategias de desarrollo personal. Es un producto informativo y educativo.

3. ENTREGA DEL PRODUCTO
Tras completar el pago, recibirás acceso inmediato al material digital. El enlace de descarga se enviará a tu correo electrónico registrado.

4. POLÍTICA DE REEMBOLSO
Debido a la naturaleza digital del producto, no ofrecemos reembolsos una vez que se ha entregado el acceso al material. Te recomendamos revisar toda la información disponible antes de realizar tu compra.

5. USO PERSONAL
El contenido adquirido es para uso personal exclusivo. Queda prohibida la reproducción, distribución, modificación o reventa del material sin autorización expresa.

6. DESCARGO DE RESPONSABILIDAD
• El Protocolo Estoico es un programa educativo y no sustituye el consejo médico profesional.
• Los resultados pueden variar según el compromiso y las circunstancias individuales de cada persona.
• Consulta con un profesional de la salud antes de iniciar cualquier programa de ejercicio físico.
• No nos hacemos responsables de lesiones o daños derivados del uso incorrecto del programa.

7. PROPIEDAD INTELECTUAL
Todo el contenido del Protocolo Estoico, incluyendo textos, diseños, gráficos y metodología, está protegido por derechos de autor. Todos los derechos reservados.

8. MODIFICACIONES
Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor desde su publicación.

9. LEY APLICABLE
Estos términos se rigen por las leyes aplicables en la jurisdicción correspondiente.
`;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-6 sm:py-8 border-t border-dark-border bg-dark-surface" data-id="element-29">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-30">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm" data-id="element-31">
            <p className="text-gray-600" data-id="element-32">
              © {new Date().getFullYear()} El Protocolo Estoico. Todos los
              derechos reservados.
            </p>
            <div className="flex items-center gap-1 text-gray-600" data-id="element-33">
              <span className="hidden sm:inline" data-id="element-34">|</span>
            </div>
            <div className="flex items-center gap-4" data-id="element-35">
              <button onClick={() => setActiveModal('privacy')} className="text-gray-500 hover:text-gold transition-colors duration-200 underline underline-offset-2 decoration-gray-700 hover:decoration-gold" data-id="element-36">
                Política de Privacidad
              </button>
              <span className="text-dark-border" data-id="element-37">•</span>
              <button onClick={() => setActiveModal('terms')} className="text-gray-500 hover:text-gold transition-colors duration-200 underline underline-offset-2 decoration-gray-700 hover:decoration-gold" data-id="element-38">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal Popup */}
      <AnimatePresence data-id="element-39">
        {activeModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50" data-id="element-40" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 30
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 30
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25
        }} className="fixed inset-4 sm:inset-8 md:inset-16 lg:inset-y-12 lg:inset-x-[15%] z-50 flex flex-col bg-dark-card border border-dark-border rounded-lg shadow-2xl overflow-hidden" data-id="element-41">
              {/* Header */}
              <div className="flex items-center justify-between px-5 sm:px-8 py-4 sm:py-5 border-b border-dark-border bg-dark-surface shrink-0" data-id="element-42">
                <div className="flex items-center gap-3" data-id="element-43">
                  <ShieldIcon className="w-5 h-5 text-gold" data-id="element-44" />
                  <h3 className="font-cinzel font-bold text-lg sm:text-xl text-white" data-id="element-45">
                    {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                  </h3>
                </div>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-dark-border/50 transition-colors" data-id="element-46">
                  <XIcon className="w-5 h-5" data-id="element-47" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-6 sm:py-8" data-id="element-48">
                <pre className="whitespace-pre-wrap font-inter text-sm sm:text-base text-gray-300 leading-relaxed" data-id="element-49">
                  {activeModal === 'privacy' ? privacyContent.trim() : termsContent.trim()}
                </pre>
              </div>

              {/* Footer */}
              <div className="px-5 sm:px-8 py-4 border-t border-dark-border bg-dark-surface shrink-0 flex justify-end" data-id="element-50">
                <button onClick={() => setActiveModal(null)} className="px-6 py-2.5 bg-gold/10 border border-gold/30 text-gold font-cinzel font-bold text-sm tracking-wider rounded-sm hover:bg-gold/20 transition-colors" data-id="element-51">
                  CERRAR
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}